import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import {
  LeasingCalculatorCodesSelectors,
  LeasingCalculatorCodesThunks,
} from 'modules/hooks/codesHooks/redux/leasingCalculatorCodes';
import {
    getLeasingObjectAgeCodeByFirstRegistrationYear, getProductionDatebyFirstRegistrationYear,
    mapToAverageTotalMileageOptionsByObjectData,
    mapToEnginesByModelAndFuelTypeId,
    mapToFuelsByModelId,
    mapToObjectMakesByObjectTypeAndObjectId,
    mapToObjectModelsByObjectIds,
    mapToObjectsByObjectTypeId,
} from 'modules/utils/mappers/leasingCalculatorCodesMapper';
import { useEffect, useMemo, useCallback } from 'react';
import { LeasingCalculatorData } from '../../common/LeasingCalculatorData';
import {
    LeasingCalculatorSelectors,
} from 'modules/leasingCalculator/redux';
import {PDV} from "../../infoCalculation/components/common/helper";

export const useLeasingCalculatorObjectOptions = (
  defaultValues?: LeasingCalculatorData,
) => {
  const dispatch = useAppDispatch();

    const premadeProducts = useAppSelector(
        LeasingCalculatorSelectors.selectPremadeProducts,
    );

    const leasingObjectAges = useAppSelector(
        LeasingCalculatorCodesSelectors.selectLeasingObjectAgeCodes,
    );

  const leasingObjectTypes = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectTypes,
  );

  const leasingObjectTypesOptions = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectTypesOptions,
  );

  const leasingObjectTypesAndMakes = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectTypesAndMakes,
  );

  const leasingObjectDataByMake = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectDataByMake,
  );

  const leasingObjectRegistrationYears = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectFirstRegistrationYears,
  );

  const leasingObjectRegistrationYearsOptions = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectFirstRegistrationYearsOptions,
  );

  useEffect(() => {
    if (!leasingObjectTypesOptions) {
      dispatch(
        LeasingCalculatorCodesThunks.fetchCombinedLeasingObjectTypesAndMakes(),
      );
    }

    if (!leasingObjectRegistrationYearsOptions) {
      dispatch(LeasingCalculatorCodesThunks.fetchLeasingCalculatorCodes());
    }
  }, [
    dispatch,
    leasingObjectRegistrationYearsOptions,
    leasingObjectTypesOptions,
  ]);

  const fetchLeasingObjectDataByMake = useCallback(
    async (makeId?: number) => {
      if (makeId) {
        dispatch(
          LeasingCalculatorCodesThunks.fetchCombinedLeasingObjectDataByMake(
            makeId,
          ),
        );
      }
      return [];
    },
    [dispatch],
  );

    const fetchLeasingRemainingValue = useCallback(
        async (currentValues) => {

            const ageValue = leasingObjectAges?.find(
                (ageCode) =>
                    ageCode.name ===
                    getLeasingObjectAgeCodeByFirstRegistrationYear(
                        defaultValues?.leasingObjectFirstRegistrationYear?.name,
                    ),
            )?.id as number;

            dispatch(
                LeasingCalculatorCodesThunks.fetchCalculatorRemainingValue(
                    {
                        financingDuration: (currentValues?.financingPeriod?.id as number) * 12,
                        participationPercentage: Number(currentValues?.operationalLeasingParticipation),
                        leasingObjectAmount: Number(currentValues?.leasingNetObjectPrice) * PDV,
                        leasingObjectAmountWithoutTaxes: Number(currentValues?.leasingNetObjectPrice),
                        leasingObjectCatalogAmount: Number(currentValues?.leasingNetObjectPrice),
                        leasingObjectMakeId: currentValues?.leasingObjectMake?.id as number,
                        leasingObjectModelId: currentValues?.leasingObjectModel?.id as number,
                        ppmvAmount: Number(currentValues?.leasingObjectPpmv),
                        leasingObjectId: currentValues?.leasingObject?.id as number,
                        ageId: ageValue,
                        productionDate: getProductionDatebyFirstRegistrationYear(
                            currentValues?.leasingObjectFirstRegistrationYear?.name,
                        ),
                        redemptionValuePercentage: 1,
                        fuelTypeId: currentValues?.leasingObjectFuel?.id as number,
                        engine:  currentValues?.leasingObjectEngine?.name,
                        allowedUsageLevel: currentValues?.averageTotalMileage?.id as number,
                        milage: Number(
                            currentValues?.currentNumberOfKilometersTraveledOrWorkingHours,
                        ),
                    }
                ),
            );
            return [];
        },
        [dispatch],
    );

  const leasingObjects = useMemo(
    () =>
      mapToObjectsByObjectTypeId(
        defaultValues?.leasingObjectType?.id,
        leasingObjectTypesAndMakes,
      ) || [],
    [leasingObjectTypesAndMakes, defaultValues?.leasingObjectType?.id],
  );

  const leasingObjectMakes = useMemo(
    () =>
      mapToObjectMakesByObjectTypeAndObjectId(
        defaultValues?.leasingObjectType?.id,
        defaultValues?.leasingObject?.id,
        leasingObjectTypesAndMakes,
      ) || [],
    [
      leasingObjectTypesAndMakes,
      defaultValues?.leasingObjectType?.id,
      defaultValues?.leasingObject?.id,
    ],
  );

  const leasingObjectModels = useMemo(
    () =>
      mapToObjectModelsByObjectIds(
        defaultValues?.leasingObjectType?.id,
        defaultValues?.leasingObject?.id,
        defaultValues?.leasingObjectMake?.id,
        leasingObjectDataByMake,
      ) || [],
    [
      defaultValues?.leasingObjectType?.id,
      defaultValues?.leasingObject?.id,
      defaultValues?.leasingObjectMake?.id,
      leasingObjectDataByMake,
    ],
  );

  const leasingObjectFuels = useMemo(
    () =>
      mapToFuelsByModelId(
        defaultValues?.leasingObjectModel?.id,
        leasingObjectDataByMake,
      ) || [],
    [leasingObjectDataByMake, defaultValues?.leasingObjectModel?.id],
  );

  const leasingObjectEngines = useMemo(
    () =>
      mapToEnginesByModelAndFuelTypeId(
        defaultValues?.leasingObjectModel?.id,
        defaultValues?.leasingObjectFuel?.id,
        leasingObjectDataByMake,
      ) || [],
    [
      leasingObjectDataByMake,
      defaultValues?.leasingObjectModel?.id,
      defaultValues?.leasingObjectFuel?.id,
    ],
  );

  const leasingObjectAverageTotalMileage = useMemo(
    () =>
      mapToAverageTotalMileageOptionsByObjectData(
        defaultValues?.leasingObjectModel?.id,
        defaultValues?.leasingObjectFuel?.id || 0,
        defaultValues?.leasingObjectEngine?.id || '',
        leasingObjectDataByMake,
      ) || [],
    [
      defaultValues?.leasingObjectEngine?.id,
      defaultValues?.leasingObjectFuel?.id,
      defaultValues?.leasingObjectModel?.id,
      leasingObjectDataByMake,
    ],
  );

  return {
    leasingObjectTypes: {
      uwtTypes: leasingObjectTypes ?? [],
      options: leasingObjectTypesOptions ?? [],
    },
    leasingObjects: {
      uwtTypes: leasingObjects.uwtTypes ?? [],
      options: leasingObjects.options ?? [],
    },
    leasingObjectMakes: {
      uwtTypes: leasingObjectMakes.uwtTypes ?? [],
      options: leasingObjectMakes.options ?? [],
    },
    leasingObjectModels: {
      uwtTypes: leasingObjectModels.uwtTypes ?? [],
      options: leasingObjectModels.options ?? [],
    },
    leasingObjectFuels: {
      uwtTypes: leasingObjectFuels.uwtTypes ?? [],
      options: leasingObjectFuels.options ?? [],
    },
    leasingObjectEngines: {
      uwtTypes: leasingObjectEngines.uwtTypes ?? [],
      options: leasingObjectEngines.options ?? [],
    },
    leasingObjectRegistrationYears: {
      uwtTypes: leasingObjectRegistrationYears ?? [],
      options: leasingObjectRegistrationYearsOptions ?? [],
    },
    leasingObjectAverageTotalMileage: {
      uwtTypes: leasingObjectAverageTotalMileage.uwtTypes ?? [],
      options: leasingObjectAverageTotalMileage.options ?? [],
    },
    fetchLeasingObjectDataByMake,
      fetchLeasingRemainingValue,
  };
};
